import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * React Router doesn't touch scroll position on navigation the way a
 * traditional multi-page site does — moving to a new route (e.g. after a
 * Stripe redirect, or from a "success" page) otherwise leaves the browser
 * wherever the previous page had scrolled to, so a page can visibly load
 * "from the middle" or "from the bottom".
 *
 * Hash links are the other half of the same problem: the browser only jumps
 * to an #id on a real document load, so a client-side navigation to /#report
 * would change the URL and move nothing. `key` is in the dependency list so
 * that following the SAME hash link twice scrolls again rather than being a
 * no-op, which is exactly what someone clicking "Report an Issue" from
 * halfway down the home page expects.
 *
 * Rendered once near the top of the router, with no output of its own.
 */
const ScrollToTop = () => {
  const { pathname, hash, key } = useLocation();

  useEffect(() => {
    if (hash) {
      // The target may not be mounted on the first paint after a route
      // change, so this runs after the browser has laid the new page out.
      const raf = requestAnimationFrame(() => {
        const target = document.getElementById(hash.slice(1));
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          window.scrollTo(0, 0);
        }
      });
      return () => cancelAnimationFrame(raf);
    }

    window.scrollTo(0, 0);
    return undefined;
  }, [pathname, hash, key]);

  return null;
};

export default ScrollToTop;
